import React, { useState } from "react";
import { Car, Phone, Mail, MapPin } from "lucide-react";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import TermsModal from "./TermsModal";
import { useTranslation } from "react-i18next";

export const Footer: React.FC = () => {
  const { t } = useTranslation();
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Car className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold">
                {t("1082", "TaxiSchipholAirport")}
              </span>
            </div>
            <p className="text-gray-400 mb-4">
              {t(
                "1083",
                "Your reliable and efficient transportation solution for travel to and from Schiphol Airport."
              )}
            </p>
            <p className="text-sm text-gray-300">
              {t("1084", "KVK Number: 69753938")}
            </p>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {t("1085", "Contact Information")}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-primary" />
                <span className="text-gray-300">+31 6 39182624</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-primary" />
                <span className="text-gray-300">
                  info@taxischipholairport.net
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-primary" />
                <span className="text-gray-300">
                  Stationsplein NO 264, 1046 AH Schiphol
                </span>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {t("1086", "Our Services")}
            </h3>
            <ul className="space-y-2 text-gray-300">
              <li>{t("1087", "Airport Transfers")}</li>
              <li>{t("1088", "Business Transportation")}</li>
              <li>{t("1089", "City Transfers")}</li>
              <li>{t("1090", "Tour Services")}</li>
              <li>{t("1091", "Hourly Bookings")}</li>
            </ul>
          </div>

          {/* Reviews */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {t("1715", "Leave a Review")}
            </h3>
            <div className="space-y-3">
              <a
                href="https://g.page/r/CcSMCW_1ucbXEBI/review"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors group"
              >
                <img
                  src="/images/Google.webp"
                  alt={t("1375", "Google Reviews")}
                  className="h-6 w-auto"
                />
                <span className="text-sm text-gray-400 group-hover:text-primary transition-colors">
                  {t("1717", "Review on Google")}
                </span>
              </a>
              <a
                href="https://www.trustpilot.com/evaluate/taxischipholairport.net"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors group"
              >
                <img
                  src="/images/tru.webp"
                  alt={t("1376", "Trustpilot Reviews")}
                  className="h-6 w-auto"
                />
                <span className="text-sm text-gray-400 group-hover:text-primary transition-colors">
                  {t("1718", "Review on Trustpilot")}
                </span>
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400">
                &copy; {currentYear} TaxiSchipholAirport.{" "}
                {t("1098", "All rights reserved.")}{" "}
              </p>
              <p className="text-sm text-gray-300 mt-1">
                {t("1099", "Powered by")}{" "}
                <a
                  href="https://siliconsquire.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-light transition-colors"
                >
                  siliconsquire.com
                </a>
              </p>
            </div>

            <div className="flex space-x-6 text-sm">
              <button
                onClick={() => setShowPrivacyModal(true)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {t("1100", "Privacy Policy")}
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={() => setShowTermsModal(true)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                {t("1101", "Terms & Conditions")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </footer>
  );
};
